// ── Enums / string unions ──────────────────────────────────────────────────────

export type ChequeType = 'Received' | 'Issued' | 'PostDated';

/**
 * The statuses a cheque can be in.
 *
 * One per member of the domain enum, and nothing else. `Realized` and `Stopped` used to be here
 * and were never in `ChequeStatus.cs`, so `ALLOWED_TRANSITIONS` offered next steps the API could
 * not accept — and `Cleared`, the normal end of a cheque's life, was not offered at all.
 *
 * `Draft` is this layer's name for the domain's `Pending`; see CHEQUE_STATUS_BY_VALUE.
 */
export type ChequeStatus =
  | 'Draft'
  | 'Issued'
  | 'Received'
  | 'Deposited'
  | 'Presented'
  | 'Cleared'
  | 'Returned'
  | 'Cancelled'
  /** Signed over to a supplier instead of being banked (ML-12). Terminal for us. */
  | 'Endorsed';

export type ChequePartyType = 'Customer' | 'Supplier' | 'Other';

/**
 * The numeric values the API actually sends for ChequeStatus.
 *
 * The domain enum is serialised by value — the API has no JsonStringEnumConverter registered — so
 * `chequeStatus` arrives as 1, not "Issued", while the type above says otherwise and every screen
 * compares it against strings. Those comparisons are all quietly false.
 *
 * Mapping here rather than switching the API to string enums: that setting is global, and every
 * enum on every endpoint would change shape at once. This is the narrow repair.
 */
const CHEQUE_STATUS_BY_VALUE: Record<number, ChequeStatus> = {
  0: 'Draft',       // Pending — post-dated, not yet live
  1: 'Issued',
  2: 'Received',
  3: 'Deposited',
  4: 'Presented',
  5: 'Cleared',
  6: 'Returned',
  7: 'Cancelled',
  8: 'Received',    // Represented — back in hand, presentable again
  9: 'Endorsed',
};

/** Reads a cheque status however the API sent it. */
export const toChequeStatus = (value: ChequeStatus | number | null | undefined): ChequeStatus => {
  if (typeof value === 'number') return CHEQUE_STATUS_BY_VALUE[value] ?? 'Draft';
  return value ?? 'Draft';
};

/**
 * The numeric values to send back, which is the other half of the same problem.
 *
 * With no `JsonStringEnumConverter` registered the API will not read an enum by name either —
 * `{"newStatus":"Deposited"}` answers 400. Writing a status means sending its number, so this is
 * the inverse of CHEQUE_STATUS_BY_VALUE rather than a second source of truth.
 *
 * `Draft` maps to `Pending`, and `Represented` has no entry: nothing in the UI sets it, and 8
 * already reads back as `Received`.
 */
const CHEQUE_STATUS_VALUE: Record<ChequeStatus, number> = {
  Draft:     0,
  Issued:    1,
  Received:  2,
  Deposited: 3,
  Presented: 4,
  Cleared:   5,
  Returned:  6,
  Cancelled: 7,
  Endorsed:  9,
};

/** The number the API expects for a status. */
export const chequeStatusValue = (status: ChequeStatus): number => CHEQUE_STATUS_VALUE[status];

// ── Status display helpers ────────────────────────────────────────────────────

export const CHEQUE_TYPE_LABELS: Record<ChequeType, string> = {
  Received:  'Received',
  Issued:    'Issued',
  PostDated: 'Post-Dated',
};

export const CHEQUE_STATUS_LABELS: Record<ChequeStatus, string> = {
  Draft:     'Draft',
  Received:  'Received',
  Deposited: 'Deposited',
  Returned:  'Returned',
  Cancelled: 'Cancelled',
  Issued:    'Issued',
  Presented: 'Presented',
  Cleared:   'Cleared',
  Endorsed:  'Endorsed',
};

export const CHEQUE_STATUS_COLORS: Record<ChequeStatus, string> = {
  Draft:     'bg-gray-100 text-gray-700',
  Received:  'bg-blue-100 text-blue-700',
  Deposited: 'bg-indigo-100 text-indigo-700',
  Returned:  'bg-orange-100 text-orange-700',
  Cancelled: 'bg-gray-200 text-gray-500',
  Issued:    'bg-blue-100 text-blue-700',
  Presented: 'bg-yellow-100 text-yellow-700',
  Cleared:   'bg-green-100 text-green-700',
  // Gone from our hands, not banked — read as done, but not as money received.
  Endorsed:  'bg-purple-100 text-purple-700',
};

// ── Detail DTO ────────────────────────────────────────────────────────────────

export interface ChequeRecordDto {
  id:                  string;
  companyId:           string;
  financialYearCode:   string;
  locationId?:         string | null;
  /** Null for a received cheque that has not yet been deposited (GAP-013). */
  bankAccountId?:      string | null;
  bankAccountCode?:    string | null;
  bankAccountName?:    string | null;
  chequeType:          ChequeType;
  chequeNumber:        string;
  chequeDate:          string;
  partyType:           ChequePartyType;
  customerId?:         string | null;
  customerCode?:       string | null;
  customerName?:       string | null;
  supplierId?:         string | null;
  supplierCode?:       string | null;
  supplierName?:       string | null;
  amount:              number;
  currencyCode:        string;
  chequeStatus:        ChequeStatus;
  referenceDocument?:  string | null;
  remarks?:            string | null;
  /** Who the cheque is payable to, as written on it (GAP-004). */
  payeeName?:          string | null;
  /** ACCOUNT_PAYEE | NOT_NEGOTIABLE | OPEN, or null when never stated (GAP-005). */
  crossingType?:       string | null;
  /** The AP payment this cheque was signed over to settle (ML-12). Null unless endorsed. */
  endorsedToPaymentId?: string | null;
  /** REPRESENTABLE or NON_REPRESENTABLE once returned (ML-11). Null until then. */
  returnClassification?: string | null;
  /** How many times it has been re-presented (ML-11). */
  representCount?:      number;
  statusChangedBy?:    string | null;
  statusChangedDate?:  string | null;
  statusChangeReason?: string | null;
  createdDate:         string;
  drawerBankId?:      string | null;
  drawerBranchId?:    string | null;
}

// ── List row DTO ──────────────────────────────────────────────────────────────

export interface ChequeRecordListDto {
  id:                 string;
  chequeNumber:       string;
  chequeDate:         string;
  chequeType:         ChequeType;
  /** Null for a received cheque that has not yet been deposited (GAP-013). */
  bankAccountCode?:   string | null;
  bankAccountName?:   string | null;
  partyType:          ChequePartyType;
  customerCode?:      string | null;
  customerName?:      string | null;
  supplierCode?:      string | null;
  supplierName?:      string | null;
  currencyCode:       string;
  amount:             number;
  chequeStatus:       ChequeStatus;
  referenceDocument?: string | null;
  createdDate:        string;
  /** The book this number came out of. Null for a received cheque, or an unmanaged account. */
  chequeBookCode?:    string | null;
  /** The customer's bank/branch a received cheque is drawn on, resolved for display (GAP-014). */
  drawerBankName?:    string | null;
  drawerBranchName?:  string | null;
}

// ── Filter DTO ────────────────────────────────────────────────────────────────

/**
 * Query for GET /api/v1/cheques.
 *
 * The names here have to match the server's ChequeFilterDto exactly, because the query binds by
 * name and silently ignores anything it does not recognise — there is no error, just an unfiltered
 * list. Two fields were wrong for as long as this file existed: `chequeStatus` (the server calls it
 * `status`) and `pageNumber` (`page`). The Returned Cheque picker was therefore offering every
 * cheque in the company rather than deposited ones, and both cheque screens always showed page one.
 */
export interface ChequeRecordFilterDto {
  companyId?:      string;
  locationId?:     string;
  bankAccountId?:  string;
  /** With bankAccountId, also include a cheque that names no bank yet (GAP-013). */
  includeUnassignedBankAccount?: boolean;
  chequeType?:     string;
  /** Server name is `status`. */
  status?:         string;
  dateFrom?:       string;
  dateTo?:         string;
  search?:         string;
  /** Server name is `page`, 1-based. */
  page?:           number;
  pageSize?:       number;
}

// ── Create / Update requests ──────────────────────────────────────────────────

export interface CreateChequeRequest {
  companyId:          string;
  financialYearCode:  string;
  locationId?:        string | null;
  bankAccountId:      string;
  chequeType:         ChequeType;
  chequeNumber:       string;
  chequeDate:         string;
  partyType:          ChequePartyType;
  amount:             number;
  currencyCode:       string;
  customerId?:        string | null;
  customerCode?:      string | null;
  customerName?:      string | null;
  supplierId?:        string | null;
  supplierCode?:      string | null;
  supplierName?:      string | null;
  referenceDocument?: string | null;
  remarks?:           string | null;
  /** Drawer (customer's) bank + branch for a received cheque (GAP-014). */
  drawerBankId?:      string | null;
  drawerBranchId?:    string | null;
  /** Who the cheque is made out to, and how it is crossed — both print on it. */
  payeeName?:         string | null;
  /** ACCOUNT_PAYEE | NOT_NEGOTIABLE | OPEN, or null for "not stated". */
  crossingType?:      string | null;
}

export interface UpdateChequeRequest {
  chequeDate:         string;
  partyType:          ChequePartyType;
  customerId?:        string | null;
  customerCode?:      string | null;
  customerName?:      string | null;
  supplierId?:        string | null;
  supplierCode?:      string | null;
  supplierName?:      string | null;
  referenceDocument?: string | null;
  remarks?:           string | null;
  /** Who the cheque is made out to, and how it is crossed — both print on it. */
  payeeName?:         string | null;
  /** ACCOUNT_PAYEE | NOT_NEGOTIABLE | OPEN, or null for "not stated". */
  crossingType?:      string | null;
}

/**
 * `newStatus`, numerically — matching
 * `record ChangeChequeStatusRequest(ChequeStatus NewStatus, string? Reason = null)`.
 *
 * This used to declare `chequeStatus` as a string union. Nothing bound to `NewStatus`, and a
 * non-nullable enum with nothing to bind to is `Pending`, so every status change on the cheque
 * screen was read as a move to Pending and refused — with a message naming a transition the user
 * had not asked for.
 */
export interface ChangeChequeStatusRequest {
  newStatus: number;
  reason?:   string | null;
}
